import { motion } from 'framer-motion';
import { useRegularProjects } from '../hooks/useProjects';
import { useTickets } from '../hooks/useTickets';
import { useUserStore } from '../store/userStore';
import { DashboardHeader } from '../components/DashboardHeader';
import { useNavigate } from 'react-router-dom';
import type { Project, Ticket, TicketStatus } from '../types';
import { RiskBadge } from '../components/RiskBadge';
import { Markdown } from '../components/Markdown';
import { CollapsibleText } from '../components/CollapsibleText';
import { AlertTriangle, CheckCircle2, Clock } from 'lucide-react';

function ProjectSkeleton() {
  return (
    <div className="bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl p-5 space-y-4">
      <div className="skeleton h-4 w-2/3 rounded" />
      <div className="skeleton h-3 w-1/3 rounded" />
    </div>
  );
}

function statusIcon(status: TicketStatus) {
  switch (status) {
    case 'at_risk':
      return <AlertTriangle className="h-4 w-4 text-red-500" />;
    case 'on_track':
      return <CheckCircle2 className="h-4 w-4 text-yellow-500" />;
    default:
      return <Clock className="h-4 w-4 text-blue-500" />;
  }
}

function statusLabel(status: TicketStatus): string {
  switch (status) {
    case 'at_risk': return 'At Risk';
    case 'on_track': return 'On Track';
    case 'planning': return 'Planning';
    case 'complete': return 'Complete';
    case 'triage': return 'Triage';
    case 'idea': return 'Idea';
    default: return status;
  }
}

function statusOrder(status: TicketStatus): number {
  switch (status) {
    case 'at_risk': return 0;
    case 'on_track': return 1;
    case 'planning': return 2;
    default: return 3;
  }
}

function MyTicketsForProject({
  project,
  userId,
}: {
  project: Project;
  userId: string;
}) {
  const { data: tickets } = useTickets(project.projectId);

  const myTickets = (tickets ?? [])
    .filter((t) => t.assigneeId === userId)
    .sort((a, b) => statusOrder(a.status) - statusOrder(b.status));

  if (myTickets.length === 0) return null;

  return (
    <>
      {myTickets.map((ticket: Ticket) => (
        <div
          key={ticket.ticketId}
          className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06]"
        >
          <span className="flex-shrink-0">{statusIcon(ticket.status)}</span>
          <div className="min-w-0 flex-1">
            <p className="text-title text-gray-900 dark:text-white truncate">
              {ticket.title}
            </p>
            <p className="text-meta text-gray-500 dark:text-gray-400">
              {project.name}
            </p>
          </div>
          <span className="flex-shrink-0 text-meta font-medium text-gray-500 dark:text-gray-400">
            {statusLabel(ticket.status)}
          </span>
        </div>
      ))}
    </>
  );
}

function ContextFeedForProject({ project }: { project: Project }) {
  const { data: tickets } = useTickets(project.projectId);

  const recent = (tickets ?? [])
    .filter((t) => t.status !== 'complete' && t.status !== 'invalid')
    .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
    .slice(0, 3);

  if (recent.length === 0) return null;

  return (
    <>
      {recent.map((ticket) => (
        <div
          key={ticket.ticketId}
          className="px-3 py-2.5 rounded-lg bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06]"
        >
          <div className="flex items-center gap-2 mb-1">
            <span className="text-meta font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wide flex-shrink-0">
              {project.name}
            </span>
            <span className="text-micro text-gray-300 dark:text-gray-600">·</span>
            <span className="text-meta text-gray-400 dark:text-gray-500 flex-shrink-0">
              {ticket.status?.replace('_', ' ')}
            </span>
          </div>
          <p className="text-title text-gray-900 dark:text-white truncate">{ticket.title}</p>
          {ticket.aiSummary && (
            <CollapsibleText key={ticket.ticketId} collapsedLines={2} className="mt-1">
              <Markdown size="xs" className="text-gray-500 dark:text-gray-400">
                {ticket.aiSummary}
              </Markdown>
            </CollapsibleText>
          )}
        </div>
      ))}
    </>
  );
}

function CompactProjectCard({ project }: { project: Project }) {
  const navigate = useNavigate();

  const statusColors: Record<string, string> = {
    at_risk: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    on_track: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    active: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    planning: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    draft: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    complete: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    archived: 'bg-gray-100 text-gray-600 dark:bg-gray-800/60 dark:text-gray-400',
  };
  const colorCls = statusColors[project.status] ?? statusColors.archived;

  return (
    <motion.div
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
      onClick={() => navigate(`/projects/${project.projectId}`)}
      className="flex items-center justify-between gap-3 px-4 py-3 bg-white dark:bg-bg-surface border border-gray-100 dark:border-white/[0.06] rounded-xl cursor-pointer hover:shadow-sm transition-shadow"
    >
      <p className="text-title text-gray-900 dark:text-white truncate flex-1">
        {project.name}
      </p>
      <span className={`flex-shrink-0 text-meta font-semibold px-2 py-0.5 rounded-full ${colorCls}`}>
        {project.status?.replace('_', ' ')}
      </span>
      <RiskBadge score={project.overallRiskScore} size="sm" />
    </motion.div>
  );
}

export function EngineerDashboard({ onBackToToday }: { onBackToToday?: () => void } = {}) {
  const { sessionConfig } = useUserStore();
  const { data: projects, isLoading } = useRegularProjects();

  const regularProjects = projects ?? [];
  const userId = sessionConfig?.userId ?? '';

  return (
    <div className="px-6 py-6 sm:px-10 lg:px-16">
      <DashboardHeader title="My Work" roleLabel="Engineering" onBackToToday={onBackToToday} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* My Tickets */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between mb-1">
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
              My Tickets
            </h2>
            <span className="text-meta text-gray-400 dark:text-gray-500">
              Showing tickets across your projects
            </span>
          </div>

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="skeleton h-14 rounded-lg" />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {regularProjects.map((project) => (
                <MyTicketsForProject
                  key={project.projectId}
                  project={project}
                  userId={userId}
                />
              ))}
              {regularProjects.length === 0 && (
                <p className="text-body font-medium text-gray-500 text-center py-8">
                  No projects found.
                </p>
              )}
            </div>
          )}

          {/* Context Feed */}
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mt-4"
          >
            <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Context Feed
            </h2>
            {isLoading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="skeleton h-16 rounded-lg" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {regularProjects.map((project) => (
                  <ContextFeedForProject key={project.projectId} project={project} />
                ))}
                {regularProjects.length === 0 && (
                  <p className="text-body font-medium text-gray-500 text-center py-6">No projects.</p>
                )}
              </div>
            )}
          </motion.div>
        </div>

        {/* My Projects */}
        <div className="space-y-4">
          <h2 className="text-meta font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
            My Projects
          </h2>
          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <ProjectSkeleton key={i} />
              ))}
            </div>
          ) : (
            <div className="space-y-4">
              {regularProjects.map((project) => (
                <CompactProjectCard key={project.projectId} project={project} />
              ))}
              {regularProjects.length === 0 && (
                <p className="text-body font-medium text-gray-500 text-center py-8">
                  No projects.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
